import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useModal } from 'src/contexts/modalContext'
import { useNotifications } from 'src/contexts/notificationContext'
import { Column, Table, TableButton } from 'src/DAIComponents/Table/DAITable'
import { DAIRequest } from 'src/utils/DAIRequest'
import CreateAppointmentTypeModal from './CreateAppointmentTypeModal'
import DAIButton from 'src/DAIComponents/Input/Button/DAIButton'
import { AppointmentTypeForm } from '../SettingsTypes'
import NewRemoveModal from 'src/DAIComponents/Modal/NewRemoveModal'

type AppoointmentType = {
  name: string
  id: string
  appointmentBookingLink: string
  daysInAdvance: number
  defaultMeetingLength: number
}

export default function AppointmentTypeSettings() {
  const { addNotification } = useNotifications()
  const { openModal, closeModal } = useModal()

  const {
    data: appointmentTypes,
    isLoading,
    refetch,
  } = useQuery<AppoointmentType[]>({
    queryKey: ['appointmentTypes'],
    queryFn: async () =>
      (await DAIRequest('GET', '/api/v1/appointmentTypes/')).data,
  })

  const { mutateAsync: onDelete } = useMutation({
    mutationFn: async (id: string) =>
      (
        await DAIRequest('DELETE', '/api/v1/appointmentTypes/:id', {
          params: {
            id,
          },
        })
      ).data,
    onSuccess: () => addNotification('success', 'Sucessfully deleted'),
    onError: () => addNotification('error', 'Failed to delete'),
  })

  const { mutateAsync: onCreate } = useMutation({
    mutationFn: async (data: AppointmentTypeForm) =>
      (
        await DAIRequest('POST', '/api/v1/appointmentTypes/', {
          body: data,
        })
      ).data,
    onSuccess: () => addNotification('success', 'Sucessfully created'),
    onError: () => addNotification('error', 'Failed to create'),
  })

  const { mutateAsync: onUpdate } = useMutation({
    mutationFn: async (mutatationData: {
      data: AppointmentTypeForm
      id: string
    }) =>
      (
        await DAIRequest('PUT', '/api/v1/appointmentTypes/:id', {
          params: { id: mutatationData.id },
          body: mutatationData.data,
        })
      ).data,
    onSuccess: () => addNotification('success', 'Sucessfully updated'),
    onError: () => addNotification('error', 'Failed to update'),
  })

  const handleUpdate = useCallback(
    async (data: AppointmentTypeForm, id: string) => {
      await onUpdate({ data, id })
      await refetch()
      closeModal()
    },
    [onUpdate, refetch, closeModal]
  )

  const onEdit = useCallback(
    async (data: AppointmentTypeForm, id: string) => {
      openModal(
        <CreateAppointmentTypeModal
          initialData={data}
          onSubmit={data => handleUpdate(data, id)}
          onClose={closeModal}
        />
      )
    },
    [handleUpdate, closeModal]
  )

  const handleCopy = useCallback(
    (appointmentBookingLink: string) => {
      navigator.clipboard
        .writeText(appointmentBookingLink)
        .then(() => {
          addNotification('success', 'Copied Successfully!')
        })
        .catch(err => {
          addNotification('error', 'Failed to copy.')
        })
    },
    [addNotification]
  )

  const handleCreate = useCallback(
    async (data: AppointmentTypeForm) => {
      await onCreate(data)
      await refetch()
      closeModal()
    },
    [onCreate, refetch, closeModal]
  )

  const onAdd = useCallback(() => {
    openModal(
      <CreateAppointmentTypeModal
        onSubmit={handleCreate}
        onClose={closeModal}
      />
    )
  }, [openModal, handleCreate, closeModal])

  const handleDelete = useCallback((id: string) => {
    openModal(
      <NewRemoveModal
        closeModal={closeModal}
        onDelete={async () => {
          await onDelete(id)
          await refetch()
          closeModal()
        }}
        title={'Delete Appointment Type'}
        text={
          'Are you sure you want to remove this appointment type? Any previous booking pages will no longer work.'
        }
      />)
  }, [openModal, closeModal])

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <Table
        loading={isLoading}
        onAdd={onAdd}
        data={appointmentTypes ?? []}
        title={'Appointment Types'}
        description={'A list of all your appointment event types.'}
      >
        <Column title="Name">{(at: AppoointmentType) => at.name}</Column>
        <Column title="Default Meeting Length">
          {(at: AppoointmentType) => at.defaultMeetingLength}
        </Column>
        <Column title="Days In Advance">
          {(at: AppoointmentType) => at.daysInAdvance}
        </Column>
        <Column title="">
          {(at: AppoointmentType) => (
            <TableButton
              editButton={true}
              onClick={async () => await onEdit(at, at.id)}
            />
          )}
        </Column>
        <Column title="">
          {(at: AppoointmentType) => (
            <TableButton
              deleteButton={true}
              onClick={async () => await handleDelete(at.id)}
            />
          )}
        </Column>
        <Column title="">
          {(at: AppoointmentType) => (
            <DAIButton
              onClick={() => handleCopy(at.appointmentBookingLink)}
              text="Copy Booking Link"
              buttonType="button"
            />
          )}
        </Column>
      </Table>
    </div>
  )
}
